import {CancelablePromise} from './cancellable';
import {createAsyncThunk} from '@reduxjs/toolkit';
import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {enqueueSnackbar} from 'notistack';
import i18n from "../../../i18n";
import {GENERAL_OBJECT} from "@/components/types";
import {cleanRedirectTo} from "@/components/shared/client/utils";

export const createAsyncActionCreator =
    (sliceName: string) =>
        <T, R>(func: (...args: Array<[T] extends [never] ? void : T>) => Promise<R>, actionName: string) => {
            return createAsyncThunk(`${sliceName}/${actionName}`, async (...args: [[T] extends [never] ? void : T]) => {
                return func(...args);
            });
        };

export const createCancellableAsyncAction = <T extends (data: Parameters<T>[0]) => CancelablePromise<any>>(requestFn: T, actionName: string) => {
    return createAsyncThunk<Awaited<ReturnType<T>>, Parameters<T>[0]>(actionName, async (data, {signal}) => {
        const request = requestFn(data);

        signal.addEventListener('abort', () => {
            request.cancel();
        });

        return request;
    });
};

function hasErrorMessage(e: any): e is Error {
    return e && e.message && typeof e.message === 'string';
}

interface CustomError {
    status: 'CUSTOM_ERROR';
    message: string;
    ini: string;
    localesData: {
        [key: string]: any
    },
    params?: any
}

export interface RTKError {
    error: CustomError;
}

export class RtkErrorClass implements RTKError {
    error: CustomError;

    constructor(message: string, ini: string, localesData: GENERAL_OBJECT, params: GENERAL_OBJECT) {
        this.error = {
            status: 'CUSTOM_ERROR',
            message,
            ini,
            localesData,
            params
        };
    }
}

const unexpectedErrorText = 'Unexpected error, please contact administrator.';

export function extractErrorFromFetchBaseQuery(err: FetchBaseQueryError) {
    if ('error' in err) {
        return err.error;
    }

    if ('data' in err) {
        return JSON.stringify(err.data);
    }

    return unexpectedErrorText;
}

export const extractRtkError = (err: any, objectProperties?: { errorSnackbar?: boolean }): RTKError => {
    const errorMessage = hasErrorMessage(err) ? err.message : unexpectedErrorText;
    if (err.response?.data?.redirectTo) {
        window.location.href = cleanRedirectTo(err.response?.data?.redirectTo);
        return new RtkErrorClass("", "", {}, {})
    }
    if (objectProperties?.errorSnackbar) {
        enqueueSnackbar(i18n.t(`errors.${err?.response?.data?.message}`, err?.response?.data?.localesData ?? {}) as string, {variant: 'error'});
    }
    return new RtkErrorClass(err?.response?.data?.message ?? errorMessage, err?.response?.data?.ini ?? "", err?.response?.data?.localesData ?? {}, err.config.params ?? null);

};

export const rtkActionHandler = async <T>(promise: Promise<T>, options: {
    snackbar?: boolean
} = {snackbar: false}): Promise<{ data?: T; error?: any }> => {
    try {
        const data = await promise;
        return {data};
    } catch (err) {
        return extractRtkError(err, {
            errorSnackbar: options.snackbar,
        });
    }
};
